import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Store, ArrowLeft } from 'lucide-react';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">{title}</h2>
    <div className="text-gray-600 space-y-2 leading-relaxed">{children}</div>
  </div>
);

export default function RefundPolicy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-600 rounded-lg flex items-center justify-center">
              <Store className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-gray-900">KiranaFlow</span>
          </Link>
          <Link to="/home" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Refund Policy</h1>
          <p className="text-sm text-gray-400 mb-10">Effective date: 15 May 2026 · Last updated: 15 May 2026</p>

          <p className="text-gray-600 mb-8 leading-relaxed">
            This Refund Policy governs all subscription payments made to KiranaFlow. Please read it carefully
            before subscribing. By paying for a KiranaFlow subscription you confirm that you have read,
            understood, and agreed to this policy.
          </p>

          <Section title="1. Subscription Model">
            <p>KiranaFlow subscriptions are offered on a monthly or annual basis. Payment is currently collected manually (UPI, bank transfer, or other agreed method) before your subscription is activated or renewed.</p>
            <p>Subscriptions are activated upon confirmation of receipt of payment and access is granted immediately. Because digital access is provided instantly upon activation, subscription fees are generally non-refundable except as described below.</p>
          </Section>

          <Section title="2. When You May Request a Refund">
            <p>You may request a refund in the following circumstances:</p>
            <ul className="list-disc ml-5 space-y-2 mt-2">
              <li>
                <strong className="text-gray-800">Service not delivered:</strong> You made a payment but your account was not activated within 48 hours of confirmed payment, and you have not begun using the Service.
              </li>
              <li>
                <strong className="text-gray-800">Duplicate payment:</strong> A technical or human error caused you to be charged more than once for the same subscription period. We will refund the duplicate payment in full.
              </li>
              <li>
                <strong className="text-gray-800">Extended unplanned outage:</strong> The Service was unavailable for more than 72 consecutive hours due to issues on our end (excluding scheduled maintenance, third-party infrastructure failures, or force majeure events), and you did not use the Service during that period. In this case, we may offer a prorated credit toward your next billing cycle.
              </li>
              <li>
                <strong className="text-gray-800">Annual plan — within 7 days:</strong> If you subscribed to an annual plan and have not used the Service beyond the initial setup (no sales billed, no stock uploaded other than the onboarding upload), you may request a full refund within 7 calendar days of activation.
              </li>
            </ul>
          </Section>

          <Section title="3. Non-Refundable Situations">
            <p>Refunds will <strong className="text-gray-800">not</strong> be issued in the following cases:</p>
            <ul className="list-disc ml-5 space-y-1 mt-2">
              <li>You changed your mind after the subscription was activated</li>
              <li>You did not use the Service during the subscription period</li>
              <li>Your account was suspended due to violation of our <Link to="/terms" className="text-emerald-600 underline">Terms of Service</Link></li>
              <li>Downgrade requests mid-cycle (you retain access to the higher tier until the current billing period ends)</li>
              <li>Partial months or partial years — subscriptions are not prorated on cancellation (except where stated above)</li>
              <li>Requests made after 7 calendar days from the payment date (for monthly plans) or after the 7-day window for annual plans</li>
            </ul>
          </Section>

          <Section title="4. How to Request a Refund">
            <p>To request a refund, contact us within the applicable window:</p>
            <ul className="list-disc ml-5 space-y-1 mt-2">
              <li>Email <a href="mailto:rohankakad712@gmail.com" className="text-emerald-600 underline">rohankakad712@gmail.com</a> with the subject line "Refund Request — [Your Shop Name]"</li>
              <li>Include: your registered email or mobile number, the payment reference / UTR number, the reason for your request</li>
              <li>WhatsApp: +91 88888 55212</li>
            </ul>
            <p className="mt-2">We will acknowledge your request within 2 business days and communicate our decision within 7 business days.</p>
          </Section>

          <Section title="5. Refund Processing">
            <p>Approved refunds will be returned via the same payment method used for the original transaction, or via UPI to the account you specify. Processing time after approval is typically 5–10 business days depending on your bank or payment provider.</p>
          </Section>

          <Section title="6. Cancellation">
            <p>You may cancel your subscription at any time by contacting us. Cancellation takes effect at the end of your current billing period — you will retain full access until then. No refund is issued for the unused portion of the current billing period (unless you qualify under Section 2 above).</p>
          </Section>

          <Section title="7. Changes to This Policy">
            <p>We may update this Refund Policy from time to time. Changes take effect when posted. Subscriptions already active at the time of a change continue under the policy in effect at the time of purchase.</p>
          </Section>

          <Section title="8. Contact Us">
            <p>For any refund-related questions:</p>
            <p className="mt-2">
              <strong>KiranaFlow</strong><br />
              Email: <a href="mailto:rohankakad712@gmail.com" className="text-emerald-600 underline">rohankakad712@gmail.com</a><br />
              Phone / WhatsApp: +91 88888 55212<br />
              Response time: 2 business days
            </p>
          </Section>

          <div className="mt-10 pt-6 border-t border-gray-100 flex flex-wrap gap-4 text-sm text-gray-400">
            <Link to="/terms"   className="hover:text-emerald-600 transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-emerald-600 transition-colors">Privacy Policy</Link>
            <Link to="/home"    className="hover:text-emerald-600 transition-colors">Back to Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
